import { Link } from 'react-router-dom';

interface NewsCardProps {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  imageUrl: string;
  author: string;
  publishDate: string;
  readTime: string;
  size?: 'default' | 'large' | 'small';
}

export default function NewsCard({
  id,
  title,
  excerpt,
  category,
  categorySlug,
  imageUrl,
  author,
  publishDate,
  readTime,
  size = 'default',
}: NewsCardProps) {
  if (size === 'large') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="relative w-full h-[420px] md:h-[480px] overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-block px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full mb-3">
              {category}
            </span>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50 mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
              {title}
            </h2>
            <p className="text-background-200 text-sm line-clamp-2 mb-3">{excerpt}</p>
            <div className="flex items-center gap-4 text-xs text-background-300">
              <span>{author}</span>
              <span>{publishDate}</span>
              <span>{readTime}阅读</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (size === 'small') {
    return (
      <Link
        to={`/article/${id}`}
        className="group flex gap-4 p-3 bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img src={imageUrl} alt={title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs text-accent-600 font-medium">{category}</span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-500 transition-colors">
            {title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{publishDate}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full">
          {category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors mb-2">
          {title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{author}</span>
          <div className="flex items-center gap-3">
            <span>{publishDate}</span>
            <span>{readTime}阅读</span>
          </div>
        </div>
      </div>
    </Link>
  );
}